/**
 * Represents an action that will be taken when a particular event occurs.
 */
export interface IEvent {
    /**
     * Adds and returns a function that will be called when the event takes place.
     * @param handler The function to add.
     */
    on(handler: () => void): () => void
    /**
     * Removes a previously added function.
     * @param handler The function to remove.
     */
    off(handler: () => void): void

    /**
     * Removes all handlers
     */
    clear(): void
}
/**
 * Represents an action that will be taken when a particular event occurs.
 */
export class Event implements IEvent {
    private _handlers: { (): void }[] = [] // array of functions

    /**
     * Adds and returns a function that will be called when the event takes place.
     * @param handler The function to add.
     */
    public on(handler: () => void): () => void {
        this._handlers.push(handler)
        return handler
    }
    /**
     * Removes a previously added function.
     * @param handler The function to remove.
     */
    public off(handler: () => void): void {
        this._handlers = this._handlers.filter(h => h != handler)
    }
    /**
     * Raises the event, calling all handler functions that have been added.
     */
    public raise(): void {
        for (var index = 0, count = this._handlers.length; index < count; ++index) {
            this._handlers[index]()
        }
    }

    /**
     * Removes all handlers
     */
    public clear(): void {
        this._handlers.clearArray()
    }
}

/**
 * Represents an action that will be taken when a particular event occurs, passing related data to any handler functions.
 */
export interface IDataEvent<T> {
    /**
     * Adds and returns a function that will be called when the event takes place.
     * @param handler The function to add.
     */
    on(handler: (data: T) => void): (data: T) => void
    /**
     * Removes a previously added function.
     * @param handler The function to remove.
     */
    off(handler: (data: T) => void): void

    /**
     * Removes all handlers
     */
    clear(): void
}
/**
 * Represents an action that will be taken when a particular event occurs.
 */
export class DataEvent<T> implements IDataEvent<T> {
    private _handlers: { (data: T): void }[] = [] // array of functions

    /**
     * Adds and returns a function that will be called when the event takes place.
     * @param handler The function to add.
     */
    public on(handler: (data: T) => void): (data: T) => void {
        this._handlers.push(handler)
        return handler
    }
    /**
     * Removes a previously added function.
     * @param handler The function to remove.
     */
    public off(handler: (data: T) => void): void {
        this._handlers = this._handlers.filter(h => h != handler)
    }
    /**
     * Raises the event, calling all handler functions that have been added with the specified data.
     * @param data The data to pass to the handler functions.
     */
    public raise(data: T): void {
        for (var index = 0, count = this._handlers.length; index < count; ++index) {
            this._handlers[index](data)
        }
    }

    /**
     * Removes all handlers
     */
    public clear(): void {
        this._handlers.clearArray()
    }
}
